import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { VendorService } from 'src/app/services/Vendor.service';
import { ANGULAR_MODULES, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { DatePipe } from '@angular/common';
import { PhoneFormatPipe } from 'src/app/Shared/phone-format.pipe';

@Component({
  selector: 'app-ops-vendor-notes',
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, FormsModule, PhoneFormatPipe, WordReplacePipe],
  providers: [DatePipe],
  standalone: true,
  templateUrl: './ops-vendor-notes.html',
  styleUrl: './ops-vendor-notes.scss'
})

export class OpsVendorNotes implements OnInit, OnDestroy {

  @Input() vendorId!: number;
  private destroy$ = new Subject<void>();
  vendorNoteForm!: FormGroup;
  vendorInfo: any;
  vendorMarketID = '';
  vendorName = '';
  telephone = '';
  Email = '';
  address = '';
  selectedNote: any = null;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  notes: string = '';
  NotesData: any[] = [];
  maxExpiryDate: Date = new Date();

  constructor(
    private vendorService: VendorService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.getDefaultDateRange();
    this.getVendorInfo();
    this.getVendorNotes();
  }

  initForm(): void {
    this.vendorNoteForm = this.fb.group({
      VendorNotesId: [null],
      Notes: ['', [Validators.required, Validators.maxLength(200)]],
      NotesDateTime: [new Date()]
    });
  }

  getDefaultDateRange(): void {
    const today = new Date();
    this.toDate = today;
    this.fromDate = today;
  }

  onFromDateChange(): void {

    if (!this.fromDate || !this.toDate) {
      return;
    }

    if (this.fromDate > this.toDate) {

      Swal.fire({
        icon: 'warning',
        title: 'Invalid Date Range',
        text: 'From Date cannot be later than To Date.'
      });

      this.fromDate = this.toDate;
      return;
    }

    this.filterNotes();
  }

  private filterNotes(): void {

    if (!this.fromDate || !this.toDate) {
      this.getVendorNotes();
      return;
    }

    if (this.fromDate > this.toDate) {
      return;
    }

    const formattedFrom = this.datePipe.transform(this.fromDate, 'yyyy-MM-dd');
    const formattedTo = this.datePipe.transform(this.toDate, 'yyyy-MM-dd');

    this.reloadVendorNotes(formattedFrom!, formattedTo!);
  }

  onToDateChange(): void {

    if (!this.fromDate || !this.toDate) {
      return;
    }

    if (this.toDate < this.fromDate) {

      Swal.fire({
        icon: 'warning',
        title: 'Invalid Date Range',
        text: 'To Date cannot be earlier than From Date.'
      });

      this.toDate = this.fromDate;
      return;
    }

    this.filterNotes();
  }


  getVendorInfo() {
    this.vendorService.GetVendorById(this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.vendorInfo = res;
          this.vendorMarketID = res.MarketVendorId;
          this.vendorName = res.VendorName;
          this.telephone = `${res.Telephone}`;
          this.Email = res.Email;
          this.address = res.Address + ' ' + res.Address2;
        },
        error: (err) => console.error('Failed to load vendor info', err)
      });
  }

  getVendorNotes() {
    this.vendorService.getVendorNotesById(this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const mapped = res.map((n: any) => ({
            VendorNotesId: n.VendorNotesId,
            NotesDateTime: n.VnoDtmEntryDate ? new Date(n.VnoDtmEntryDate) : null,
            EnteredBy: n.VnoUserName,
            Notes: n.VendorNotes
          }));
          this.NotesData = [...mapped];
        },
        error: (err) => console.error('Failed to load vendor notes', err)
      });
  }

  reloadVendorNotes(fromDate: string, toDate: string): void {
    this.vendorService.getVendorNotesById(this.vendorId, fromDate, toDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const mapped = res.map((n: any) => ({
            VendorNotesId: n.VendorNotesId,
            NotesDateTime: n.VnoDtmEntryDate ? new Date(n.VnoDtmEntryDate) : null,
            EnteredBy: n.VnoUserName,
            Notes: n.VendorNotes
          }));
          this.NotesData = [...mapped];
        },
        error: (err) => console.error('Failed to load vendor notes', err)
      });
  }

  saveVendorNote() {
    if (this.vendorNoteForm.invalid) {
      this.vendorNoteForm.markAllAsTouched();
      this.toastr.warning("Please enter a note", "Invalid", this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    const formValue = this.vendorNoteForm.value;
    if (this.selectedNote &&
      formValue.VendorNotesId === this.selectedNote.VendorNotesId &&
      formValue.Notes.trim() === this.selectedNote.Notes.trim()) {
      this.toastr.info("No changes ", "Info", this.sharedHTMLSettings.ToasterConfig);
      return;
    }
    const noteData: any = {
      VendorId: this.vendorId,
      VendorNotes: formValue.Notes.trim(),
      VnoDtmEntryDate: formValue.NotesDateTime
    };

    if (formValue.VendorNotesId) {
      noteData.VendorNotesId = formValue.VendorNotesId;
    }

    this.vendorService.saveVendorNote(noteData).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.Success) {
          this.toastr.success(res.Message, "Success", this.sharedHTMLSettings.ToasterConfig);
        } else {
          this.toastr.warning(res.Message, "Invalid", this.sharedHTMLSettings.ToasterConfig);
        }

        this.getVendorNotes();
        this.vendorNoteForm.reset({
          VendorNotesId: null,
          Notes: '',
          NotesDateTime: new Date()
        });
        this.selectedNote = null;
        this.closeModalById('vendorNoteModal');
      },
      error: (err) => {
        this.toastr.error("Failed to save vendor note", "Error", this.sharedHTMLSettings.ToasterConfig);
        console.error('Error saving note', err);
      }
    });
  }

  deleteVendorNote(vendorNoteId: number) {
    if (!vendorNoteId) return;
    Swal.fire({
      title: 'Are you sure?',
      text: 'This note will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (result.isConfirmed) {
        this.vendorService.deleteVendorNote(vendorNoteId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              if (res.Success) {
                this.toastr.success(res.Message || 'Vendor note deleted successfully', 'Success', this.sharedHTMLSettings.ToasterConfig);
                this.getVendorNotes();
              } else {
                this.toastr.warning(res.Message || 'Unable to delete vendor note', 'Warning', this.sharedHTMLSettings.ToasterConfig);
              }
            },
            error: (err) => {
              this.toastr.error(err?.error?.message || 'Failed to delete vendor note', 'Error',
                this.sharedHTMLSettings.ToasterConfig
              );
              console.error(err);
            }
          });
      }
    });
  }

  onRowSelected(args: any): void {
    const rowData = args.data;
    if (!rowData) return;

    this.selectedNote = { ...rowData };
    this.vendorNoteForm.patchValue({
      VendorNotesId: rowData.VendorNotesId,
      Notes: rowData.Notes,
      NotesDateTime: new Date()
    });

    this.openModalById('vendorNoteModal')
  }

  addVendorNote(): void {
    this.resetNoteForm();
    this.openModalById('vendorNoteModal')
  }

  closeModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const modalInstance = window['bootstrap'].Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
    document.body.classList.remove('modal-open');
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  openModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      let modal = window['bootstrap'].Modal.getInstance(modalEl);
      if (!modal) {
        modal = new window['bootstrap'].Modal(modalEl, { backdrop: 'static', keyboard: false });
      }
      modal.show();
    }
  }

  resetNoteForm() {
    this.vendorNoteForm.reset({
      VendorNotesId: null,
      Notes: '',
      NotesDateTime: new Date()
    });
    this.selectedNote = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
